/*
 * ELIO | Save Cart on Abandonment
 * Draft theme companion for Omnisend custom-trigger form.
 * This is NOT an automatic popup on add-to-cart.
 * Identifies likely abandonment, then offers to email a Shopify cart permalink.
 * No PII or cart contents are stored in localStorage/sessionStorage.
 * Requires enabled Omnisend form + enabled custom-event automation to send email.
 */
(function () {
  'use strict';
  if (window.__ELIO_SAVE_CART_BOOTED__) return;
  window.__ELIO_SAVE_CART_BOOTED__ = true;

  var config = window.ELIO_SAVE_CART_CONFIG || {};
  var formID = String(config.formID || '').trim();
  if (!/^[a-f0-9]{24}$/.test(formID)) return;

  var SIGNUP_KEY = 'elio-known-email-signup-v1';
  var PRIMARY_SIGNUP_KEY = 'elio-primary-form-submitted-v1';
  var primaryFormID = String(config.primaryFormID || '').trim();
  var LAST_DISPLAY_KEY = 'elio-saved-cart-display-v1';
  var TWO_DAYS = 2 * 24 * 60 * 60 * 1000;
  var path = (window.location.pathname || '').toLowerCase();
  if (/\/(checkout|checkouts|account|orders|password)(\/|$)/.test(path) || /thank.you/.test(path)) return;

  var cart = null;
  var cartArrivedAt = 0;
  var currentFormVisible = false;
  var otherFormVisible = false;
  var otherFormOpenAt = 0;
  var lastPrimaryFormClosedAt = 0;
  var pageOpenedAt = Date.now();
  var triggeredThisPage = false;
  var submissionHandled = false;
  var pendingSavedCartUrl = '';
  var pendingCartCount = 0;

  function storeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function storeSet(key, value) {
    try { window.localStorage.setItem(key, String(value)); } catch (e) { /* storage blocked */ }
  }
  function cookieHasOmnisendSubmission() {
    // Created by Omnisend upon any form completion on this browser.
    try { return /(?:^|;\s*)omnisend-form-[a-f0-9]{24}-filled-at=/.test(document.cookie || ''); }
    catch (e) { return false; }
  }
  function contactCookiePresent() {
    // Omnisend's contact-ID cookie differs from its anonymous session cookie.
    // Recognize returning identified contacts who may never have used the popup
    // (for example, shoppers arriving from an Omnisend marketing email).
    try { return /(?:^|;\s*)omnisendContactID=[^;]+/.test(document.cookie || ''); }
    catch (e) { return false; }
  }
  function primaryFormSubmitted() {
    if (storeGet(PRIMARY_SIGNUP_KEY)) return true;
    if (!/^[a-f0-9]{24}$/.test(primaryFormID)) return false;
    try {
      return (document.cookie || '').split(';').some(function (raw) {
        return raw.trim().indexOf('omnisend-form-' + primaryFormID + '-filled-at=') === 0;
      });
    } catch (e) { return false; }
  }
  function alreadyIdentified() {
    // Client-side checks can only recognize known sessions. If an anonymous
    // shopper enters an email belonging to an existing Omnisend contact,
    // Omnisend itself displays the Already Subscribed result after submission;
    // we must STILL dispatch the saved-cart event for that contact.
    return Boolean(config.hasShopifyEmail) || primaryFormSubmitted() ||
      Boolean(storeGet(SIGNUP_KEY)) || contactCookiePresent() ||
      cookieHasOmnisendSubmission();
  }
  function cartHasItems() {
    return Boolean(cart && cart.item_count > 0 && Array.isArray(cart.items) && cart.items.length > 0);
  }
  function cartLink(fromCart) {
    if (!fromCart || !Array.isArray(fromCart.items)) return '';
    var counts = Object.create(null);
    var count = 0;
    fromCart.items.forEach(function (line) {
      var id = String(line.variant_id || '');
      var qty = Number(line.quantity || 0);
      if (!/^\d+$/.test(id) || !Number.isSafeInteger(qty) || qty < 1) return;
      counts[id] = (counts[id] || 0) + qty;
      count += qty;
    });
    if (!count || !Object.keys(counts).length) return '';
    // Shopify ?storefront=true explicitly loads the online store CART, not checkout.
    // A new cart is populated with the exact variant IDs and quantities saved.
    // Shopify cart permalinks don't preserve line-item properties/selling plans.
    var parts = Object.keys(counts).map(function (id) { return id + ':' + counts[id]; });
    return window.location.origin + '/cart/' + parts.join(',') + '?storefront=true';
  }
  function hasOpenDialog() {
    if (currentFormVisible || otherFormVisible) return true;
    // Guard against any other visible storefront overlays, including the cart drawer.
    var els = document.querySelectorAll('[role="dialog"], dialog[open], .dialog--open, .drawer--open, [aria-modal="true"]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.closest && el.closest('[hidden], [aria-hidden="true"]')) continue;
      var st = window.getComputedStyle(el);
      if (st.display === 'none' || st.visibility === 'hidden' || !el.getClientRects().length) continue;
      var rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth) return true;
    }
    return false;
  }
  function eligible() {
    var now = Date.now();
    if (triggeredThisPage || alreadyIdentified() || !cartHasItems()) return false;
    if (now - pageOpenedAt < 6000 || now - cartArrivedAt < 1500) return false;
    if (currentFormVisible || (now - otherFormOpenAt < 5000) || (now - lastPrimaryFormClosedAt < 5000)) return false;
    if (hasOpenDialog() || document.hidden || document.visibilityState === 'hidden') return false;
    if (document.activeElement && /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName)) return false;
    if ((now - Number(storeGet(LAST_DISPLAY_KEY) || 0)) < TWO_DAYS) return false;
    return Boolean(cartLink(cart));
  }
  function displayForm() {
    if (!eligible()) return;
    // One immediate read to avoid showing a stale cart after an AJAX mutation.
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (fresh) {
        if (!fresh || triggeredThisPage) return;
        cart = fresh;
        if (!eligible()) return;
        pendingSavedCartUrl = cartLink(fresh);
        pendingCartCount = fresh.item_count;
        if (!pendingSavedCartUrl) return;
        triggeredThisPage = true;
        // Only start the two-day cooldown if Omnisend actually renders the form.
        window.omnisend = window.omnisend || [];
        window.omnisend.push(['openForm', formID]);
      }).catch(function () {});
  }
  function acceptCart(next) {
    if (!next || !Array.isArray(next.items)) return;
    var wasEmpty = !cartHasItems();
    cart = next;
    if (cartHasItems() && (!cartArrivedAt || wasEmpty)) cartArrivedAt = Date.now();
    if (!cartHasItems()) { cartArrivedAt = 0; pendingSavedCartUrl = ''; }
  }

  // DESKTOP: genuine browser-chrome exit motion. Not an arbitrary scrolling delay.
  document.addEventListener('mouseout', function (event) {
    if (!window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches) return;
    if (event.relatedTarget || event.toElement || event.clientY > 8) return;
    displayForm();
  });

  // Mobile abandonment popup disabled. Returning to page top is not reliable exit intent.

  // The existing ELIO cart-state broadcast is reused when present.
  document.addEventListener('elio:cart:state', function (event) {
    if (event && event.detail) acceptCart(event.detail.cart);
  });
  function refreshCart() {
    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(acceptCart).catch(function () {});
  }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) refreshCart(); });
  window.addEventListener('pageshow', refreshCart);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshCart);
  else refreshCart();

  // Observe Omnisend signup status, and preserve the cart snapshot at the time
  // the visitor chooses "Save my cart". Never transmit any event before submit.
  window.addEventListener('omnisendForms', function (event) {
    var d = event && event.detail || {};
    var type = d.type || d.eventName;
    var id = d.form && String(d.form.id || '');
    if (type === 'view') {
      if (id === formID) { currentFormVisible = true; storeSet(LAST_DISPLAY_KEY, Date.now()); }
      else { otherFormVisible = true; otherFormOpenAt = Date.now(); }
    }
    if (type === 'close') {
      if (id === formID) currentFormVisible = false;
      else { otherFormVisible = false; lastPrimaryFormClosedAt = Date.now(); }
    }
    if (type !== 'submit') return;
    // A successful primary signup permanently excludes this browser from the
    // secondary save-cart capture. Submitting the save-cart form also prevents
    // repeat solicitation but must NOT block the submission in progress.
    if (id === primaryFormID) storeSet(PRIMARY_SIGNUP_KEY, '1');
    storeSet(SIGNUP_KEY, '1');
    if (id !== formID || submissionHandled || !pendingSavedCartUrl) return;
    var email = String((d.formValues && d.formValues.emailField) || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    submissionHandled = true;
    var savedUrl = pendingSavedCartUrl;
    var savedCount = pendingCartCount;
    // Identify/associate the address even if it is an existing subscriber.
    // Do not gate the event on first-subscription status. The Omnisend form
    // itself chooses the new-subscriber vs already-subscribed confirmation.
    try {
      if (window.omnisend && typeof window.omnisend.identifyContact === 'function') {
        window.omnisend.identifyContact({ email: email });
      }
    } catch (e) { /* Omnisend's form submission is still authoritative */ }
    setTimeout(function () {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['track', 'elio_saved_cart', {
        origin: 'api',
        properties: { savedCartUrl: savedUrl, itemCount: savedCount }
      }]);
    }, 550);
  });

  // Support test harnesses and theme preview diagnostics without publishing.
  window.ElioSavedCart = {
    getStatus: function () { return { eligible: eligible(), hasCart: cartHasItems(), identified: alreadyIdentified(), primaryFormSubmitted: primaryFormSubmitted(), formID: formID, formOpened: triggeredThisPage, mobileExitDisabled: true }; },
    buildLink: cartLink,
    refreshCart: refreshCart
  };
})();
